/**
 * LicenseContext — Global license state for RetailCLIQ ERP.
 *
 * Fetches a FULL license bundle from /api/license/bundle after login:
 *   { licensed, status, daysLeft, message, expiryDate, compName, clientId,
 *     tier, tierName, price, currency, billingCycle,
 *     modules[], subModules[], screens[],
 *     accessUnrestricted, availableTiers[] }
 *
 * Plus access-check helpers and renew/upgrade modal control.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { API_BASE, getToken } from '../lib/api';

const DEFAULT_STATE = {
  // license
  licensed: true,
  status: 'Live',              // 'Live' | 'Trial' | 'Expired' | 'Unknown'
  daysLeft: null,
  message: '',
  expiryDate: null,
  expiryDateHbs: null,
  compName: null,
  clientId: null,
  industry: null,
  // tier
  tier: 'Lite',
  tierName: 'RetailCLIQ Lite',
  price: 0,
  currency: 'INR',
  billingCycle: 'free',
  // privileges
  modules: [],
  subModules: [],
  screens: [],
  accessUnrestricted: true,
  privSource: 'none',
  // catalog
  availableTiers: [],
  // meta
  loaded: false,
  loading: false,
};

const LicenseContext = createContext({
  ...DEFAULT_STATE,
  refresh: (_clientId) => {},
  isModuleAllowed: (_id) => true,
  isSubModuleAllowed: (_id) => true,
  isScreenAllowed: (_id) => true,
  openUpgradeModal: () => {},
  closeUpgradeModal: () => {},
  upgradeOpen: false,
});

export function LicenseProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const refresh = useCallback(async (clientId) => {
    if (!clientId) return;
    setState(s => ({ ...s, loading: true }));
    try {
      const res = await fetch(
        `${API_BASE}/api/license/bundle?clientId=${encodeURIComponent(clientId)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const data = await res.json();
      if (data.success) {
        setState({
          licensed:           data.licensed,
          status:             data.status,
          daysLeft:           data.daysLeft,
          message:            data.message,
          expiryDate:         data.expiryDate,
          expiryDateHbs:      data.expiryDateHbs,
          compName:           data.compName,
          clientId:           data.clientId,
          industry:           data.industry,
          tier:               data.tier || 'Lite',
          tierName:           data.tierName || data.tier || 'RetailCLIQ Lite',
          price:              data.price || 0,
          currency:           data.currency || 'INR',
          billingCycle:       data.billingCycle || 'free',
          modules:            Array.isArray(data.modules)    ? data.modules    : [],
          subModules:         Array.isArray(data.subModules) ? data.subModules : [],
          screens:            Array.isArray(data.screens)    ? data.screens    : [],
          // Trust the API directly. Enterprise/unknown clients already get accessUnrestricted:true from backend.
          accessUnrestricted: data.accessUnrestricted === true,
          privSource:         data.privSource || 'none',
          availableTiers:     Array.isArray(data.availableTiers) ? data.availableTiers : [],
          loaded:             true,
          loading:            false,
        });
      } else {
        setState(s => ({ ...s, loaded: true, loading: false }));
      }
    } catch {
      // Fail-open
      setState(s => ({ ...s, loaded: true, loading: false }));
    }
  }, []);

  // Build O(1) lookup sets
  const moduleSet    = useMemo(() => new Set(state.modules.map(String)),    [state.modules]);
  const subModuleSet = useMemo(() => new Set(state.subModules.map(String)), [state.subModules]);
  const screenSet    = useMemo(() => new Set(state.screens.map(String)),    [state.screens]);

  const isModuleAllowed = useCallback((id) => {
    if (state.accessUnrestricted) return true;
    if (!id) return true;
    return moduleSet.has(String(id));
  }, [state.accessUnrestricted, moduleSet]);

  const isSubModuleAllowed = useCallback((id) => {
    if (state.accessUnrestricted) return true;
    if (!id) return true;
    return subModuleSet.has(String(id));
  }, [state.accessUnrestricted, subModuleSet]);

  const isScreenAllowed = useCallback((id) => {
    if (state.accessUnrestricted) return true;
    if (!id) return true;
    return screenSet.has(String(id));
  }, [state.accessUnrestricted, screenSet]);

  const openUpgradeModal  = useCallback(() => setUpgradeOpen(true),  []);
  const closeUpgradeModal = useCallback(() => setUpgradeOpen(false), []);

  return (
    <LicenseContext.Provider value={{
      ...state,
      refresh,
      isModuleAllowed, isSubModuleAllowed, isScreenAllowed,
      openUpgradeModal, closeUpgradeModal, upgradeOpen,
    }}>
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() { return useContext(LicenseContext); }

/**
 * LicenseBanner — shows when license is Expired or Trial (expiring soon).
 */
export function LicenseBanner({ theme }) {
  const { licensed, status, message, daysLeft, loaded, openUpgradeModal, accessUnrestricted } = useLicense();
  const [dismissed, setDismissed] = useState(false);
  const isDark = theme === 'dark';

  if (!loaded || dismissed) return null;
  // Enterprise / fully-unrestricted clients → never show the banner
  if (accessUnrestricted) return null;
  const showBanner = !licensed || status === 'Expired' ||
    (status === 'Trial' && daysLeft !== null && daysLeft <= 30);
  if (!showBanner) return null;
  const isExpired = !licensed || status === 'Expired';

  return (
    <div data-testid="license-banner" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      padding: '6px 16px',
      background: isExpired ? (isDark ? '#3f1010' : '#fef2f2') : (isDark ? '#3f2c00' : '#fffbeb'),
      borderBottom: `1px solid ${isExpired ? (isDark ? '#7f1d1d' : '#fecaca') : (isDark ? '#7c5500' : '#fde68a')}`,
      flexShrink: 0, zIndex: 100,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13 }}>{isExpired ? '⚠️' : '🕐'}</span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          color: isExpired ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#fcd34d' : '#92400e'),
          letterSpacing: '0.02em',
        }}>
          {isExpired
            ? 'LICENSE EXPIRED — Renew now to keep access.'
            : message}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={openUpgradeModal}
          data-testid="license-renew-btn"
          style={{
            background: isExpired ? '#dc2626' : '#d97706',
            color: '#fff', border: 'none', borderRadius: 6,
            padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}
        >
          {isExpired ? 'Renew Now' : 'Upgrade'}
        </button>
        <button
          onClick={() => setDismissed(true)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14,
            color: isExpired ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#fcd34d' : '#92400e'),
            padding: '0 4px', lineHeight: 1, flexShrink: 0,
          }}
          title="Dismiss"
        >×</button>
      </div>
    </div>
  );
}

/**
 * LicenseFooterBadge — sidebar footer chip with status + expiry + tier.
 */
export function LicenseFooterBadge({ theme, expanded = true }) {
  const { licensed, status, daysLeft, expiryDate, tier, tierName, loaded, accessUnrestricted } = useLicense();
  const isDark = theme === 'dark';

  // Enterprise / unrestricted: always show "Licensed" green badge
  const isExpired   = !accessUnrestricted && (!licensed || status === 'Expired');
  const isTrial     = !accessUnrestricted && status === 'Trial';
  const expiringSoon = !accessUnrestricted && status === 'Live' && daysLeft !== null && daysLeft <= 30;

  const chipColor = isExpired   ? (isDark ? '#fca5a5' : '#dc2626')   :
                    isTrial     ? (isDark ? '#fbbf24' : '#d97706')    :
                    expiringSoon ? (isDark ? '#fdba74' : '#ea580c')   :
                                  (isDark ? '#4ade80' : '#16a34a');

  const dotColor  = isExpired ? '#ef4444' : isTrial ? '#f59e0b' : '#10b981';
  const bgColor   = isExpired ? (isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.06)') :
                    isTrial   ? (isDark ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)') :
                                (isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)');
  const borderColor = isExpired ? (isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.2)') :
                      isTrial   ? (isDark ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.2)') :
                                  (isDark ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.15)');

  const label = !loaded ? 'Checking...' : isExpired ? 'Expired' : isTrial ? 'Trial' : 'Licensed';
  const subText = !loaded ? '' :
                  isExpired   ? 'Contact RetailCLIQ to renew' :
                  isTrial && daysLeft !== null ? `Trial — ${daysLeft}d left` :
                  expiringSoon && daysLeft !== null ? `Expires in ${daysLeft}d` :
                  expiryDate ? `Valid till ${new Date(expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` :
                  'Valid';

  if (!expanded) {
    return (
      <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgColor, border: `1px solid ${borderColor}` }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, boxShadow: isExpired ? `0 0 6px ${dotColor}` : 'none', animation: isExpired ? 'none' : 'lf_pulse 2.5s ease-in-out infinite' }} />
        <style>{`@keyframes lf_pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      </div>
    );
  }

  return (
    <div data-testid="license-footer-badge" style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8, padding: '7px 10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0, boxShadow: isExpired ? `0 0 6px ${dotColor}90` : 'none', animation: isExpired ? 'none' : 'lf_pulse 2.5s ease-in-out infinite' }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: chipColor, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>
        </div>
        <span style={{ fontSize: 9, fontFamily: 'monospace', color: isDark ? '#475569' : '#94a3b8', fontWeight: 600 }}>{tier || '—'}</span>
      </div>
      <div style={{ fontSize: 9, color: isExpired ? chipColor : (isDark ? '#64748b' : '#94a3b8'), marginTop: 3, lineHeight: 1.4, fontWeight: isExpired ? 600 : 500 }}>{subText}</div>
      {tierName && <div style={{ fontSize: 9, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2, fontWeight: 500 }}>{tierName}</div>}
      {isExpired && <div style={{ fontSize: 8, color: isDark ? '#f87171' : '#dc2626', marginTop: 4, fontWeight: 700, lineHeight: 1.3 }}>Contact: support@retailcliq.com</div>}
      <style>{`@keyframes lf_pulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
    </div>
  );
}

export default LicenseContext;
